
import { ChangeDetectorRef, Component, OnInit, OnDestroy, input, inject, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CheckInService, ToastMessageService, BiostartEnrollmentService, ClientService, TimeconversionService, AccessControlService } from '@fitness-force/api';
import { AccessControlVendor, ClientStatus } from '@fitness-force/enum';
import type { CheckInDetails, ClientById, ClientCheckIn, ClientCheckInResponse, TENANTSINTERFACE, TENANT_MODEL, BiostartEnrollmentStatusResponse, BiostartDevice, BiostartScanRequest, BiostartScanResponse, FaceScanResponse, FingerprintScanResponse, BiostartEnrollmentSaveRequest, CardScanResponse, AccessControlConfigResponse, ReactivateClientResponse } from '@fitness-force/models';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TENANTS_INFO } from '@fitness-force/state';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { StyleClassModule } from 'primeng/styleclass';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import type { OverlayOptions } from 'primeng/api';
import { BehaviorSubject, Observable } from 'rxjs';

@Component({
  selector: 'fitness-force-client-checkin-tab',
  templateUrl: './client-checkin-tab.component.html',
  styleUrls: ['./client-checkin-tab.component.css'],
  standalone: true,
  providers: [ToastMessageService],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    AvatarModule,
    TagModule,
    StyleClassModule,
    BlockUIModule,
    TooltipModule,
    SkeletonModule,
    DatePickerModule,
    DialogModule,
    SelectModule,
    TableModule,
    BadgeModule,
    ButtonModule,
    DateTimeFormatPipe,
    TranslateModule
],
})
export class ClientCheckinTabComponent implements OnInit, OnDestroy {
  readonly cd = inject(ChangeDetectorRef);
  private readonly checkInService = inject(CheckInService);
  private readonly biostartEnrollmentService = inject(BiostartEnrollmentService);
  private readonly accessControlService = inject(AccessControlService);
  readonly translate = inject(TranslateService);
  private readonly store = inject(Store);
  readonly toastMessageService = inject(ToastMessageService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly clientService = inject(ClientService);
  private readonly timeService = inject(TimeconversionService);

  readonly clientId = input<number>(0);
  readonly client = input<ClientById | null>(null);
  showSkeleton$ = new BehaviorSubject<boolean>(false);
  selectedDates: Date[] = [
    new Date(2024, 4, 1), // March 1, 2023
    new Date(2024, 5, 15), // March 15, 2023
    // Add more preselected dates as needed
  ];
  tableData = new BehaviorSubject<ClientCheckIn[]>([]);
  checkInDetails: CheckInDetails = {} as CheckInDetails;
  checkinParams = new Map();
  pageNumber: number = 1;
  pageSize: number = 999;
  clientCheckInResponse: ClientCheckInResponse = {} as ClientCheckInResponse;
  getAccessTenantList$: Observable<any> | any;
  tenants: TENANT_MODEL[] = [];
  calendarData: Date[][] = [[], [], []]; // Initialize with three empty arrays
  defaultDates: {month:Date, dates:Date[] | null, hasActualData: boolean, disabledDates?: Date[]}[] = [];
  
  // Individual view dates for each calendar
  calendar1ViewDate: Date = new Date();
  calendar2ViewDate: Date = new Date();
  calendar3ViewDate: Date = new Date();
  
  // Static test dates for each calendar
  staticCalendar1Dates: Date[] = [];
  staticCalendar2Dates: Date[] = [];
  staticCalendar3Dates: Date[] = [];
  loadingTable: boolean = true;
  tenantId: number = 0;
  btnLoading: boolean = false;

  // Biostar Enrollment Properties
  enrollmentStatus: BiostartEnrollmentStatusResponse | null = null;
  availableDevices: BiostartDevice[] = [];
  showEnrollmentDialog: boolean = false;
  selectedEnrollmentType: 'face' | 'fingerprint' | 'card' | null = null;
  selectedDevice: BiostartDevice | null = null;
  isScanning: boolean = false;
  scanResult: BiostartScanResponse | null = null;
  scanPreviewImage: any[] = [];
  scanPreviewData: Array<{image: string, quality?: string}> = [];
  enrollmentLoading: boolean = false;
  enrollmentForm: FormGroup;
  availableEnrollmentTypes: Array<{value: 'face' | 'fingerprint' | 'card', label: string}> = [];
  readonly deviceSelectOverlayOptions: OverlayOptions = {
    appendTo: 'body',
    autoZIndex: true,
    baseZIndex: 10001,
  };

  // Enhanced enrollment state properties
  showEnrollmentTile: boolean = true;
  enrollmentUnavailableMessage: string = '';
  enrollmentErrorType: string = '';
  enrollmentStatusLoading: boolean = true;

  // Sync functionality properties
  showSyncButton = true; // Set to true for testing
  isSyncing = false;
  selectedTenant: any = null;

  // Airfob Reactivation Properties
  showReactivateButton = signal<boolean>(false);
  isReactivating = signal<boolean>(false);
  accessControlConfig = signal<AccessControlConfigResponse[]>([]);
  accessControlLoading = signal<boolean>(true);

  // Subscription for cleanup
  private tenantSubscription: any;

  constructor() {
    this.enrollmentForm = this.fb.group({
      device: [null, Validators.required],
      enrollmentType: [null, Validators.required]
    });
  }

  ngOnInit(): void {
    this.initializeViewDates();
    this.showSkeleton$.next(true);
    this.enrollmentStatusLoading = true; // Start enrollment status loading

    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);
    this.tenantSubscription = this.getAccessTenantList$.subscribe((tenant: TENANTSINTERFACE) => {
      this.tenants = tenant.all_tenant;
      this.tenantId = tenant.selected_tenant.id;
      this.selectedTenant = tenant.selected_tenant;

      // Check if sync button should be shown
      ////this.checkSyncButtonVisibility();

      // Call checkEnrollmentStatus after tenantId is set to avoid race condition
      this.checkEnrollmentStatus();
      
      // Check access control configuration for Airfob reactivation
      this.checkAccessControlConfig();
    });

    this.checkinParams.set('from_date', {
      paramName: 'from_date',
      paramValue: this.getLastThreeMonthsStartDate().toISOString(),
    });
    this.checkinParams.set('to_date', {
      paramName: 'to_date',
      paramValue: this.getCurrentMonthLastDate().toISOString(),
    });
    this.checkinParams.set('PageNumber', {
      paramName: 'PageNumber',
      paramValue: this.pageNumber,
    });
    this.checkinParams.set('PageSize', {
      paramName: 'PageSize',
      paramValue: this.pageSize,
    });

    this.getClientCheckins();
  }

  ngOnDestroy(): void {
    // Clean up subscriptions to prevent memory leaks
    if (this.tenantSubscription) {
      this.tenantSubscription.unsubscribe();
    }
  }

  initializeViewDates(): void {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    // Calculate months properly handling year boundaries
    const months = [];
    for (let i = 2; i >= 0; i--) {
      let month = currentMonth - i;
      let year = currentYear;
      
      if (month < 0) {
        month += 12;
        year -= 1;
      }
      
      months.push({ month, year });
    }

    // Set initial view dates for each calendar (last 3 months)
    this.calendar1ViewDate = new Date(months[0].year, months[0].month, 1); // 2 months ago
    this.calendar2ViewDate = new Date(months[1].year, months[1].month, 1); // 1 month ago
    this.calendar3ViewDate = new Date(months[2].year, months[2].month, 1); // current month

  }

  getClientCheckins() {
    this.checkInService
      .getCheckInsByClientId(this.clientId(), this.getAllParams)
      .subscribe({
        next: (response: ClientCheckInResponse) => {
          this.clientCheckInResponse = response;
          this.tableData.next(response.page?.items ?? []);
          this.checkInDetails = response.statistic || {};
          this.groupCheckInDatesByMonth();
          this.loadingTable = false;
          this.showSkeleton$.next(false);
          this.cd.detectChanges();
        },
        error: () => {
          this.showSkeleton$.next(false);
          this.loadingTable = false;
          this.translate.get('MIGRATION.ERROR_MSG').subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
          this.cd.detectChanges();
        },
      });
  }

  groupCheckInDatesByMonth() {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    // Clear previous data
    this.calendarData = [[], [], []];
    this.defaultDates = [];

    // Check if we have any check-in data
    const hasCheckInData = this.clientCheckInResponse.page?.items?.length > 0;

    // Generate the last three months
    for (let i = 2; i >= 0; i--) {
      let month = currentMonth - i;
      let year = currentYear;
      
      // Adjust year and month if month goes negative
      if (month < 0) {
        month += 12;
        year -= 1;
      }
      
      const monthEnd = new Date(year, month + 1, 0);
      
      const monthsData: {month:Date, dates:Date[] | null, hasActualData: boolean, disabledDates?: Date[]} = {
        month: new Date(year, month, 1),
        dates: null,
        hasActualData: false,
        disabledDates: []
      }


      // Only process dates if we have check-in data
      if (hasCheckInData) {
        const monthDates = [];
        
        // Populate the monthDates array with dates for the month
        for (let d = new Date(year, month, 1); d <= monthEnd; d.setDate(d.getDate() + 1)) {
          monthDates.push(new Date(d));
        }

        // Filter dates for which there are events
        // Group by actual displayed date (considering timezone)
        const eventDatesInMonth: number[] = [];
        const seenDates = new Set<number>();
        
        this.clientCheckInResponse.page.items.forEach((record) => {
          const recordDate = this.timeService.ConvertUTCToLocalDateTime(record.checkin_date_time);
          
          // Check if this date falls within the current month we're processing
          if (recordDate.getFullYear() === year && recordDate.getMonth() === month) {
            const dayOfMonth = recordDate.getDate();
            if (!seenDates.has(dayOfMonth)) {
              seenDates.add(dayOfMonth);
              eventDatesInMonth.push(dayOfMonth);
            }
          }
        });
        
        const eventDates = eventDatesInMonth;

        // Generate all dates for the month
        const allMonthDates = [];
        for (let d = new Date(year, month, 1); d <= monthEnd; d.setDate(d.getDate() + 1)) {
          allMonthDates.push(new Date(d));
        }

        // Only show dates with actual check-ins
        if (eventDates.length > 0) {
          const filteredDates = monthDates.filter((date) =>
            eventDates.includes(date.getDate())
          );
          this.calendarData[i] = filteredDates;
          monthsData.dates = filteredDates;
          monthsData.hasActualData = true;
          
          // Disable all dates except the actual check-in dates
          monthsData.disabledDates = allMonthDates.filter((date) =>
            !eventDates.includes(date.getDate())
          );
        } else {
          // No events for this month, set first date of month to ensure calendar displays correct month
          const firstDateOfMonth = new Date(year, month, 1);
          this.calendarData[i] = [firstDateOfMonth];
          monthsData.dates = [firstDateOfMonth];
          monthsData.hasActualData = false;
          
          // Disable all dates in the month since there are no check-ins
          monthsData.disabledDates = allMonthDates;
        }
      } else {
        // No check-in data at all, set first date of month to ensure calendar displays correct month
        const firstDateOfMonth = new Date(year, month, 1);
        this.calendarData[i] = [firstDateOfMonth];
        monthsData.dates = [firstDateOfMonth];
        monthsData.hasActualData = false;
        
        // Generate all dates for the month and disable them all
        const allMonthDates = [];
        for (let d = new Date(year, month, 1); d <= new Date(year, month + 1, 0); d.setDate(d.getDate() + 1)) {
          allMonthDates.push(new Date(d));
        }
        monthsData.disabledDates = allMonthDates;
      }

      this.defaultDates.push(monthsData);
    }

    // Sort defaultDates by month/year (chronological order)
    this.defaultDates.sort((a, b) => {
      const yearA = a.month.getFullYear();
      const yearB = b.month.getFullYear();
      const monthA = a.month.getMonth();
      const monthB = b.month.getMonth();

      // Compare years first
      if (yearA !== yearB) {
        return yearA - yearB;
      }
      // If years are the same, compare months
      return monthA - monthB;
    });

    // Set individual view dates for each calendar
    if (this.defaultDates.length >= 3) {
      this.calendar1ViewDate = this.defaultDates[0].month;
      this.calendar2ViewDate = this.defaultDates[1].month;
      this.calendar3ViewDate = this.defaultDates[2].month;
      
    }
  }



  markCheckedIn() {
    this.btnLoading = true;
    const payload = { event_type: "CheckIn", access_status_reason: "", machine_id: "" };
    this.checkInService.markClientCheckedIn(this.tenantId, this.clientId(), payload).subscribe(
      {
        next: (res: any) => {
          if (res?.id) {
            if (res.access_status?.toLowerCase() == 'denied') {
              this.translate.get('ADMIN.CHECK_IN.CHECK_IN_DENIED')
                .subscribe((translation) => {
                  this.toastMessageService.showErrorMessage(translation);
                });
                this.btnLoading = false;
            }
            else {
              if (payload.event_type === "CheckIn") {
                this.translate
                  .get([
                    'ADMIN.CHECK_IN.CHECK_IN_SUCCESS',
                    'ADMIN.CHECK_IN.CHECK_IN_SUCCESS_MESSAGE',
                  ])
                  .subscribe((translations) => {
                    this.toastMessageService.showCustomSuccess(translations['ADMIN.CHECK_IN.CHECK_IN_SUCCESS'], translations['ADMIN.CHECK_IN.CHECK_IN_SUCCESS_MESSAGE']);
                  });
                  this.btnLoading = false;
              } else {
                this.translate
                  .get([
                    'ADMIN.CHECK_IN.CHECK_OUT_SUCCESS',
                    'ADMIN.CHECK_IN.CHECK_OUT_SUCCESS_MESSAGE',
                  ])
                  .subscribe((translations) => {
                    this.toastMessageService.showCustomSuccess(translations['ADMIN.CHECK_IN.CHECK_OUT_SUCCESS'], translations['ADMIN.CHECK_IN.CHECK_OUT_SUCCESS_MESSAGE']);
                  });
                  this.btnLoading = false;
              };
              this.getClientCheckins();
              this.cd.detectChanges();
            }

          }
        }, error: (error: any) => {
          this.btnLoading = false;
          this.toastMessageService.showError(error);
          this.cd.detectChanges();
        }
      }
    )
  }

  get showSkeleton() {
    return this.showSkeleton$.value;
  }

  get getAllParams(): Array<any> {
    return [...this.checkinParams.values()];
  }

  public get clientStatus() {
    return ClientStatus;
  }

  checkedInTenant(tenantId: number) {
    return this.tenants.find((o) => o.id == tenantId) || { name_primary_language: 'Unknown' };
  }

  getLastThreeMonthsStartDate(): Date {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    // Calculate the month and year for the last three months
    let lastThreeMonthsMonth = currentMonth - 2; // Subtract 2 for the last three months
    let lastThreeMonthsYear = currentYear;

    // Adjust year if needed
    if (lastThreeMonthsMonth < 0) {
      lastThreeMonthsMonth += 12; // 12 months in a year
      lastThreeMonthsYear -= 1;
    }

    // Set the date to the first day of the calculated month
    const lastThreeMonthsStartDate = new Date(
      lastThreeMonthsYear,
      lastThreeMonthsMonth,
      1
    );

    return lastThreeMonthsStartDate;
  }

  getCurrentMonthLastDate(): Date {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    // Calculate the last day of the current month
    const lastDate = new Date(currentYear, currentMonth + 1, 0);

    return lastDate;
  }

  // Biostar Enrollment Methods
  
  checkEnrollmentStatus() {
    const clientId = this.clientId();
    if (this.tenantId && clientId) {
      this.enrollmentStatusLoading = true;
      this.biostartEnrollmentService.getEnrollmentStatus(this.tenantId, clientId)
        .subscribe({
          next: (response: BiostartEnrollmentStatusResponse) => {
            this.enrollmentStatus = response;
            // Reset error states on success
            this.showEnrollmentTile = true;
            this.enrollmentUnavailableMessage = '';
            this.enrollmentErrorType = '';
            this.enrollmentStatusLoading = false;
            this.cd.detectChanges();
          },
          error: (error: any) => {
            console.error('Error checking enrollment status:', error);

            // Handle different error scenarios
            let errorCode = null;
            let _identifier = null;
            let httpStatus = null;

            // Try different error response structures
            if (error?.error?.errorCode) {
              errorCode = error.error.errorCode;
              _identifier = error.error.identifier;
              httpStatus = error?.status || error?.originalError?.status;
            } else if (error?.errorCode) {
              errorCode = error.errorCode;
              httpStatus = error?.status || error?.originalError?.status;
            } else {
              // Check for HTTP status in different locations
              httpStatus = error?.status || error?.originalError?.status || error?.error?.status;
            }


            if (errorCode) {
              switch (errorCode) {
                case 'suprema_config_not_found':
                  // CASE 1: No Suprema Configuration found - Hide the tile
                  this.showEnrollmentTile = false;
                  this.enrollmentStatus = null;
                  this.enrollmentUnavailableMessage = '';
                  this.enrollmentErrorType = '';
                  break;

                case 'login_failed':
                  // CASE 2: Login failed - Show alert message, no button
                  // This can occur with both HTTP 409 and HTTP 500 status codes
                  this.showEnrollmentTile = true;
                  this.enrollmentStatus = {
                    is_enrolled: false,
                    enrolled_credentials: []
                  };
                  this.enrollmentUnavailableMessage = 'BIOSTAR.ENROLLMENT_UNAVAILABLE';
                  this.enrollmentErrorType = 'login_failed';
                  break;

                case 'suprema_user_not_found':
                  // CASE 3: User not found in Suprema - Show Add Enrollment (no change needed)
                  this.showEnrollmentTile = true;
                  this.enrollmentStatus = {
                    is_enrolled: false,
                    enrolled_credentials: []
                  };
                  this.enrollmentUnavailableMessage = '';
                  this.enrollmentErrorType = '';
                  break;

                default:
                  // Default error handling
                  this.showEnrollmentTile = true;
                  this.enrollmentStatus = {
                    is_enrolled: false,
                    enrolled_credentials: []
                  };
                  this.enrollmentUnavailableMessage = '';
                  this.enrollmentErrorType = '';
                  break;
              }
            } else {
              // Check for HTTP status-based handling when errorCode is not present
              if (httpStatus === 404) {
                // CASE 1: HTTP 404 - No Suprema Configuration found - Hide the tile
                this.showEnrollmentTile = false;
                this.enrollmentStatus = null;
                this.enrollmentUnavailableMessage = '';
                this.enrollmentErrorType = '';
              } else if (httpStatus === 409 || httpStatus === 500) {
                // Fallback: If we get 409 or 500 without proper errorCode, assume login issue
                this.showEnrollmentTile = true;
                this.enrollmentStatus = {
                  is_enrolled: false,
                  enrolled_credentials: []
                };
                this.enrollmentUnavailableMessage = 'BIOSTAR.ENROLLMENT_UNAVAILABLE';
                this.enrollmentErrorType = 'login_failed';
              } else {
                // Generic error handling
                this.showEnrollmentTile = true;
                this.enrollmentStatus = {
                  is_enrolled: false,
                  enrolled_credentials: []
                };
                this.enrollmentUnavailableMessage = '';
                this.enrollmentErrorType = '';
              }
            }

            this.enrollmentStatusLoading = false;
            this.cd.detectChanges();
          }
        });
    }
  }

  openEnrollmentDialog() {
    this.showEnrollmentDialog = true;
    this.loadAvailableDevices();
  }

  loadAvailableDevices() {
    if (this.tenantId) {
      this.biostartEnrollmentService.getSupremaDevices(this.tenantId)
        .subscribe({
          next: (response: BiostartDevice[]) => {
            this.availableDevices = response ? response.filter(o => o.status.toLowerCase() == 'active') : [];
            this.cd.detectChanges();
          },
          error: (error) => {
            this.availableDevices = [];
            this.selectedDevice = null;
            console.error('Error loading devices:', error);
            this.translate.get('BIOSTAR.ERROR_LOADING_DEVICES').subscribe((translation) => {
              this.toastMessageService.showErrorMessage(translation);
            });
          }
        });
    }
  }

  onDeviceSelected() {
    const selectedDevice = this.enrollmentForm.get('device')?.value;
    this.selectedDevice = selectedDevice;
    
    if (selectedDevice) {
      // Determine available enrollment types based on device capabilities
      this.availableEnrollmentTypes = this.getAvailableEnrollmentTypes(selectedDevice);
      
      // Reset enrollment type selection
      this.enrollmentForm.get('enrollmentType')?.setValue(null);
      this.selectedEnrollmentType = null;
      this.cd.detectChanges();
    }
  }

  private getAvailableEnrollmentTypes(device: BiostartDevice): Array<{value: 'face' | 'fingerprint' | 'card', label: string}> {
    // You can modify this logic based on device properties like device.status, device.name, or other capabilities
    // For now, we'll determine based on device name patterns or assume all devices support all types
    
    const enrollmentTypes = [];
    
    // Most biostar devices support fingerprint
    if (device.status.toLowerCase() === 'active') { 
      if(device.supported_types && device.supported_types.includes('Fingerprint')) {
        enrollmentTypes.push({ value: 'fingerprint' as const, label: 'Fingerprint' });
      }
      if (device.supported_types && device.supported_types.includes('Face')) {
        enrollmentTypes.push({ value: 'face' as const, label: 'Face' });
      }
      if (device.supported_types && device.supported_types.includes('Card')) {
        enrollmentTypes.push({ value: 'card' as const, label: 'Card' });
      }
    }
    return enrollmentTypes;
  }

  onEnrollmentTypeSelected() {
    const selectedType = this.enrollmentForm.get('enrollmentType')?.value;
    this.selectedEnrollmentType = selectedType;
    this.cd.detectChanges();
  }

  selectEnrollmentType(enrollmentType: 'face' | 'fingerprint' | 'card') {
    this.selectedEnrollmentType = enrollmentType;
    this.enrollmentForm.get('enrollmentType')?.setValue(enrollmentType);
    this.cd.detectChanges();
  }

  scanCredential() {
    if (!this.selectedDevice || !this.selectedEnrollmentType) {
      return;
    }

    this.isScanning = true;
    this.scanResult = null;
    this.scanPreviewImage = [];
    this.scanPreviewData = [];

    const scanRequest: BiostartScanRequest = {
      enrolment_type: this.selectedEnrollmentType
    };

    // Add specific options based on enrollment type
    if (this.selectedEnrollmentType === 'face') {
      scanRequest.pose_sensitivity = 9;
    } else if (this.selectedEnrollmentType === 'fingerprint') {
      scanRequest.ScanFingerprintOption = {
        enroll_quality: "80",
        raw_image: true
      };
    }

    this.biostartEnrollmentService.scanCredential(this.tenantId, this.selectedDevice.id, this.clientId(), scanRequest)
      .subscribe({
        next: (response: BiostartScanResponse) => {
          this.scanResult = response;
          if(response.isSuccess){
            this.extractPreviewImage(response);
            this.isScanning = false;
          }
          else{
            this.toastMessageService.showError(response.errorMessage);
            this.isScanning = false;
          }
          this.cd.detectChanges();
        },
        error: (error) => {
         this.toastMessageService.showError(error);
         this.isScanning = false;
         this.scanResult = null;
         this.scanPreviewImage = [];
         this.scanPreviewData = [];
         this.cd.detectChanges();
        }
      });
  }

  extractPreviewImage(scanResponse: BiostartScanResponse) {
    this.scanPreviewData = []; // Reset preview data
    
    if (this.selectedEnrollmentType === 'face') {
      const faceResponse = scanResponse as FaceScanResponse;
      if (faceResponse.data?.credentials?.faces?.[0]?.template_ex_normalized_image) {
        this.scanPreviewImage.push(`data:image/jpeg;base64,${faceResponse.data.credentials.faces[0].template_ex_normalized_image}`);
        this.scanPreviewData.push({
          image: `data:image/jpeg;base64,${faceResponse.data.credentials.faces[0].template_ex_normalized_image}`
        });
      }
    } else if (this.selectedEnrollmentType === 'fingerprint') {
      const fingerprintResponse = scanResponse as FingerprintScanResponse;
      if (fingerprintResponse.data?.FingerprintTemplates) {
        fingerprintResponse.data.FingerprintTemplates.forEach((item: any) => {
          const imageData = `data:image/jpeg;base64,${item.template_image}`;
          this.scanPreviewImage.push(imageData);
          this.scanPreviewData.push({
            image: imageData,
            quality: item.enroll_quality || 'N/A'
          });
        });
      }
      if(fingerprintResponse.data.HasExistingFingerprints){
        fingerprintResponse.data.ExistingUsers.forEach((user: any) => {
          this.toastMessageService.showWarning(`${user.name} already enrolled with ${user.access_groups.map((group: any) => group.name).join(', ')}`,6000);
        });
      }
    }
  }

  saveEnrollment() {
    if (!this.scanResult || !this.selectedEnrollmentType) {
      return;
    }

    this.enrollmentLoading = true;
    const saveRequest: BiostartEnrollmentSaveRequest = {};

    if (this.selectedEnrollmentType === 'face') {
      const faceResponse = this.scanResult as FaceScanResponse;
      if (faceResponse.data?.credentials?.faces?.[0]) {
        const faceData = faceResponse.data.credentials.faces[0];
        const templates = (faceData.templates as Array<any>).map((t) => ({ template: t.template_ex ?? t.template, credential_bin_type: t.credential_bin_type }));
        saveRequest.faces = [{
          raw_image: faceData.template_ex_normalized_image,
          templates,
          flag: faceData.flag,
          useProfile: false,
          index: 0
        }];
      }
    } else if (this.selectedEnrollmentType === 'fingerprint') {
      const fingerprintResponse = this.scanResult as FingerprintScanResponse;
      if (fingerprintResponse.data?.FingerprintTemplates) {
        saveRequest.fingerprint_templates = [{
          template0: fingerprintResponse.data.FingerprintTemplates[0].template,
          template1: fingerprintResponse.data.FingerprintTemplates[1].template || '', // Optional template1
          finger_mask: true,
          isNew: true
        }];
      }
    } else if (this.selectedEnrollmentType === 'card') {
      const cardResponse = this.scanResult as CardScanResponse;
      if (cardResponse.data?.SmartCard?.card_id) {
        saveRequest.cards = [{
          card_type: cardResponse.data.SmartCard.card_id.card_type,
          card_id: cardResponse.data.SmartCard.card_id.card_id,
          display_card_id: cardResponse.data.SmartCard.card_id.display_card_id,
          id: cardResponse.data.SmartCard.card_id.card_id // Using card_id as id
        }];
      }
    }

    // Only send the relevant enrollment type in the payload
    const finalPayload: any = {};
    if (this.selectedEnrollmentType === 'face' && saveRequest.faces) {
      finalPayload.faces = saveRequest.faces;
    } else if (this.selectedEnrollmentType === 'fingerprint' && saveRequest.fingerprint_templates) {
      finalPayload.fingerprint_templates = saveRequest.fingerprint_templates;
    } else if (this.selectedEnrollmentType === 'card' && saveRequest.cards) {
      finalPayload.cards = saveRequest.cards;
    }

    this.biostartEnrollmentService.saveEnrollment(this.tenantId, this.clientId(), finalPayload)
      .subscribe({
        next: () => {
          this.enrollmentLoading = false;
          this.showEnrollmentDialog = false;
          this.resetEnrollmentDialog();
          this.isScanning = false;
          this.scanResult = null;
          this.translate.get('BIOSTAR.ENROLLMENT_SUCCESS').subscribe((translation) => {
            this.toastMessageService.showCustomSuccess('Success', translation);
          });
          this.cd.detectChanges();
          this.checkEnrollmentStatus();
        },
        error: (error) => {
          this.enrollmentLoading = false;
          this.toastMessageService.showError(error);
          this.cd.detectChanges();
        }
      });
  }

  clearEnrollment(type: 'face' | 'fingerprint' | 'card') {
    if (!this.enrollmentStatus?.is_enrolled) {
      return;
    }

    this.enrollmentLoading = true;

    this.biostartEnrollmentService.clearEnrollment(this.tenantId, this.clientId(), type)
      .subscribe({
        next: () => {
          this.enrollmentLoading = false;
          this.isScanning= false;
          this.scanResult = null;
          this.translate.get('BIOSTAR.ENROLLMENT_CLEARED').subscribe((translation) => {
            this.toastMessageService.showCustomSuccess('Success', translation);
          });
          this.showEnrollmentDialog = false;
          this.resetEnrollmentDialog();
          // Refresh enrollment status
          this.checkEnrollmentStatus();
          this.cd.detectChanges();
        },
        error: (error) => {
          this.enrollmentLoading = false;
          this.toastMessageService.showError(error);
          this.cd.detectChanges();
        }
      });
  }

  clearSpecificEnrollment(credentialType: string) {
    if (!this.enrollmentStatus?.is_enrolled) {
      return;
    }

    this.enrollmentLoading = true;
    let enrollmentType:string= '';
    // Map credential type to enrollment type
    if (credentialType.toLowerCase().includes('fingerprint')) {
      enrollmentType = 'Fingerprint';
    } else if (credentialType.toLowerCase().includes('card')) {
      enrollmentType = 'Card';
    } else if (credentialType.toLowerCase().includes('face')) {
      enrollmentType = 'Face';
    }

    this.biostartEnrollmentService.clearEnrollment(this.tenantId, this.clientId(), enrollmentType)
      .subscribe({
        next: () => {
          this.enrollmentLoading = false;
          this.translate.get('BIOSTAR.ENROLLMENT_CLEARED').subscribe((translation) => {
            this.toastMessageService.showCustomSuccess('Success', translation);
          });
          // Refresh enrollment status
          if (this.enrollmentStatus?.enrolled_credentials) {
            this.enrollmentStatus.enrolled_credentials = this.enrollmentStatus.enrolled_credentials.filter((cred: string) => cred.toUpperCase() !== credentialType.toUpperCase());
          }
          this.checkEnrollmentStatus();
          this.cd.detectChanges();
        },
        error: (error) => {
          this.enrollmentLoading = false;
          this.toastMessageService.showError(error);
          this.cd.detectChanges();
        }
      });
  }

  getCredentialIcon(credentialType: string): string {
    const type = credentialType.toLowerCase();
    if (type.includes('fingerprint')) {
      return 'fingerprint';
    } else if (type.includes('face')) {
      return 'user';
    } else if (type.includes('card')) {
      return 'credit-card';
    }
    return 'user-circle'; // default icon
  }

  getCredentialLabel(credentialType: string): string {
    const type = credentialType.toLowerCase();
    if (type.includes('fingerprint')) {
      return 'Fingerprint';
    } else if (type.includes('face')) {
      return 'Face';
    } else if (type.includes('card')) {
      return 'Card';
    }
    return credentialType; // return original if no match
  }

  cancelScan() {
    // Clear scan-related data but keep device and enrollment type selection
    this.scanResult = null;
    this.scanPreviewImage = [];
    this.scanPreviewData = [];
    this.isScanning = false;
    this.cd.detectChanges();
  }

  resetEnrollmentDialog() {
    this.selectedEnrollmentType = null;
    this.selectedDevice = null;
    this.scanResult = null;
    this.scanPreviewImage = [];
    this.scanPreviewData = [];
    this.isScanning = false;
    this.availableDevices = [];
    this.availableEnrollmentTypes = [];
    this.enrollmentForm.reset();
  }

  onEnrollmentDialogHide(): void {
    this.showEnrollmentDialog = false;
    this.resetEnrollmentDialog();
  }

  get enrollmentStatusText(): string {
    if (!this.enrollmentStatus) {
      return 'Configure';
    }
    return this.enrollmentStatus.is_enrolled ? 'Enrolled' : 'Configure';
  }

  get enrollmentButtonText(): string {
    if (!this.enrollmentStatus) {
      return 'Add Enrollment';
    }
    return this.enrollmentStatus.is_enrolled ? 'Clear' : 'Add Enrollment';
  }

  get scanButtonText(): string {
    if (this.selectedEnrollmentType === 'face' || this.selectedEnrollmentType === 'fingerprint') {
      return 'Scan';
    }
    return 'Scan';
  }

  get scanQuality(): string {
    if (this.selectedEnrollmentType === 'fingerprint' && this.scanResult) {
      const fingerprintResult = this.scanResult as FingerprintScanResponse;
      return fingerprintResult.data?.FingerprintTemplates?.[0]?.enroll_quality || 'N/A';
    }
    return 'N/A';
  }

  //// Check if sync button should be visible
  //checkSyncButtonVisibility() {
  //  if (!this.selectedTenant) {
  //    this.showSyncButton = false;
  //    return;
  //  }
//
  //  // For now, let's always show the button for testing
  //  // Add proper logic back after testing
  //  this.showSyncButton = true;
  //  this.cd.detectChanges();
  //  
  //  // Debug logging
  //  console.log('Sync button visibility check:', {
  //    selectedTenant: this.selectedTenant,
  //    showSyncButton: this.showSyncButton,
  //    ext_cms_supported: this.selectedTenant?.ext_cms_supported
  //  });
//
  //  // Check if tenant has Suprema configured by checking if devices are available
  //  this.checkSupremaConfiguration().then(hasSuprema => {
  //    // Check if tenant has AnyTime Fitness enabled
  //    const hasAnyTimeFitnessEnabled = this.selectedTenant.ext_cms_supported !== ExternalCmsSupported.NoExternalCMS;
  //    
  //    console.log('Suprema check result:', {
  //      hasSuprema,
  //      hasAnyTimeFitnessEnabled,
  //      ext_cms_supported: this.selectedTenant.ext_cms_supported,
  //      ExternalCmsSupported_NoExternalCMS: ExternalCmsSupported.NoExternalCMS
  //    });
  //    
  //    // For testing, always show the button
  //    this.showSyncButton = true; // hasSuprema || hasAnyTimeFitnessEnabled;
  //    this.cd.detectChanges();
  //  });
  //}
//
  //// Check Suprema configuration by calling the API
  //async checkSupremaConfiguration(): Promise<boolean> {
  //  try {
  //    const devices = await firstValueFrom(this.biostartEnrollmentService.getSupremaDevices(this.tenantId));
  //    return devices && devices.length > 0;
  //  } catch (error) {
  //    // If API call fails, assume Suprema is not configured
  //    return false;
  //  }
  //}
//
  //// Handle sync to external systems
  //syncClientToAccessControl() {
  //  if (!this.clientId || this.isSyncing) {
  //    return;
  //  }
//
  //  this.isSyncing = true;
  //  
  //  this.clientService.syncClientToExternalSystems(this.clientId).subscribe({
  //    next: (response) => {
  //      this.isSyncing = false;
  //      this.translate.get('CLIENT_CHECKIN.SYNC_SUCCESS').subscribe((translation) => {
  //        this.toastMessageService.showSuccess(translation);
  //      });
  //      this.cd.detectChanges();
  //    },
  //    error: (error) => {
  //      this.isSyncing = false;
  //      this.translate.get('CLIENT_CHECKIN.SYNC_ERROR').subscribe((translation) => {
  //        this.toastMessageService.showErrorMessage(translation);
  //      });
  //      //console.error('Sync error:', error);
  //      this.cd.detectChanges();
  //    }
  //  });
  //}

  // Airfob Reactivation Methods

  /**
   * Check access control configuration to determine if Airfob reactivation is available
   */
  checkAccessControlConfig(): void {
    if (!this.tenantId) {
      this.accessControlLoading.set(false);
      return;
    }

    this.accessControlLoading.set(true);
    this.accessControlService.getTenantAccessControlConfig(this.tenantId).subscribe({
      next: (configs: AccessControlConfigResponse[]) => {
        this.accessControlConfig.set(configs);
        
        // Check if any active configuration has vendor_type "Airfob"
        const hasAirfobConfig = configs.some(
          config => config.status.toLocaleLowerCase() === 'active' && (config.vendor === AccessControlVendor.Airfob || config.vendor.toString() === AccessControlVendor[AccessControlVendor.Airfob])
        );

        // Check if client has a current_keyfob
        const clientData = this.client();
        const hasKeyfob = !!(clientData?.current_keyfob && clientData.current_keyfob.trim() !== '');

        // Show button only if both conditions are met
        this.showReactivateButton.set(hasAirfobConfig && hasKeyfob);
        this.accessControlLoading.set(false);
        this.cd.detectChanges();
      },
      error: (error: any) => {
        console.error('Error fetching access control config:', error);
        this.showReactivateButton.set(false);
        this.accessControlLoading.set(false);
        this.cd.detectChanges();
      }
    });
  }

  /**
   * Reactivate client in Airfob access control system
   */
  reactivateClient(): void {
    if (this.isReactivating() || !this.clientId() || !this.tenantId) {
      return;
    }

    this.isReactivating.set(true);

    this.clientService.reactivateAccessControl(this.tenantId, this.clientId()).subscribe({
      next: (_response: ReactivateClientResponse) => {
        this.isReactivating.set(false);
        this.translate.get('ACCESS_CONTROL.REACTIVATION_SUCCESS').subscribe((translation) => {
          this.toastMessageService.showCustomSuccess(
            'Success',
            translation || 'Client successfully reactivated in access control system'
          );
        });
        this.cd.detectChanges();
      },
      error: (error: any) => {
        this.isReactivating.set(false);
        
        // Handle specific error messages
        let errorMessage = 'ACCESS_CONTROL.REACTIVATION_ERROR';
        
        if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        this.translate.get(errorMessage).subscribe((translation) => {
          this.toastMessageService.showErrorMessage(
            translation || 'Failed to reactivate client in access control system'
          );
        });
        this.cd.detectChanges();
      }
    });
  }


}